import { useLang } from 'hooks/lang';

import { actions, isLeader, projectStatusByCode, projectStatusConfig, User } from 'entities';

import { UserPenIcon } from 'components/Icons';

import { useToast } from 'providers/Toast';
import { useSingleOwnerChange } from 'providers/OwnershipChange';
import { useProjectProfile } from '../../context';

import { MESSAGES } from '../../utils';

import { registerProjectOption } from '../options';

export default registerProjectOption({
  Icon: UserPenIcon,

  action: 'CHANGE_OWNER',

  getLabel: (t) => t('change-owner'),

  shortcut: {
    key: 'O',
    triggerKey: ['ctrl'],
  },

  useOption: ({ loggedUser, project }) => {
    const { fullBlocked, updateProject } = useProjectProfile();
    const { addToast } = useToast();

    const { t } = useLang();

    const isAssign = project.status === projectStatusConfig.unassigned.code;

    const canChangeOwner = [
      loggedUser.team.id === project.teamResponsible,
      isLeader(loggedUser),
      isAssign ? true : projectStatusByCode[project.status].canChangeOwner,
      !fullBlocked,
    ].every(Boolean);

    const changeOwner = actions.projects.changeProjectOwner.use();

    const assignProject = actions.projects.assignProject.use({
      onSuccess: updateProject,
    });

    const handleChangeClick = useSingleOwnerChange({
      text: isAssign ? t('assign-project') : t('change-project-owner'),

      currentOwner: project.owner,

      requireJustification: !isAssign,

      buttonText: isAssign ? t('assign') : undefined,

      cancel: () => !isAssign && addToast(MESSAGES.projectOwnerChangeAborted(t)),

      onConfirm: async (owner, justification) => {
        const success = await (isAssign
          ? assignProject.execute({
              id: project.id,
              owner: owner!.id,
            })
          : changeOwner.execute({
              project: project.id,
              owner: owner!.id,
              justification,
            }));

        if (!success) return;

        updateProject((old) => ({
          ...old,
          ...(isAssign ? success : { owner: owner as User }),
        }));
      },
    });

    return {
      isEnabled: canChangeOwner,

      action: handleChangeClick,

      loading: changeOwner.loading,

      getLabel: isAssign ? () => t('assign-project') : undefined,
    };
  },
});
